import React, { createContext, useContext, useReducer, useEffect } from "react";
import allQuestionsData from "@/data/questions.json";

const STORAGE_KEY = "dareme-game-state";
const PLAYERS_KEY = "dareme-players";

const saveGameState = (state) => {
  try {
    const stateToSave = {
      screen: state.screen,
      configStep: state.configStep,
      settings: state.settings,
      gameStarted: state.gameStarted,
      currentPlayerIndex: state.currentPlayerIndex,
      players: state.players,
      currentQuestion: state.currentQuestion,
      showTaskTypeSelection: state.showTaskTypeSelection,
      showQuestionDialog: state.showQuestionDialog,
      selectedPlayerIndex: state.selectedPlayerIndex,
      previousPlayerIndex: state.previousPlayerIndex,
      drawnIds: Array.from(state.drawnIds || []),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
  } catch (error) {
    console.error("Błąd zapisywania stanu gry:", error);
  }
};

const loadGameState = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
    // Spróbuj wczytać stary format graczy, jeśli nowy stan nie istnieje
    const savedPlayers = localStorage.getItem(PLAYERS_KEY);
    if (savedPlayers) {
      const playerNames = JSON.parse(savedPlayers);
      return {
        players: playerNames.map((name) => ({
          id: crypto.randomUUID(),
          name /* ...stats... */,
        })),
      };
    }
    return null;
  } catch (error) {
    console.error("Błąd wczytywania stanu gry:", error);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(PLAYERS_KEY);
    return null;
  }
};

const getInitialState = () => {
  const savedState = loadGameState();
  const baseState = {
    screen: "start",
    configStep: 1,
    players: [],
    settings: {
      gameMode: "classic",
      difficulty: [1, 2], // Domyślne wartości mogą być przydatne
      occasions: [],
      nsfwEnabled: false,
      themes: [],
      packs: [],
      autoSave: true,
    },
    gameStarted: false,
    currentPlayerIndex: 0,
    selectedPlayerIndex: null,
    showTaskTypeSelection: false,
    showQuestionDialog: false,
    previousPlayerIndex: null,
    currentQuestion: null,
    bottleSpinning: false,
    allQuestions: allQuestionsData,
    drawnIds: new Set(),
  };

  if (savedState) {
    const mergedState = {
      ...baseState,
      ...savedState,
      settings: {
        ...baseState.settings,
        ...(savedState.settings || {}),
      },
      // Upewnijmy się, że każdy gracz ma wszystkie potrzebne pola, w tym punkty
      players: (savedState.players || []).map((p) => ({
        id: p.id || crypto.randomUUID(),
        name: p.name || "Gracz", // Domyślna nazwa, jeśli brakuje
        skipsUsed: p.skipsUsed || 0,
        tasksCompleted: p.tasksCompleted || 0,
        tasksSkipped: p.tasksSkipped || 0,
        points: p.points || 0, // <<< WCZYTAJ LUB USTAW PUNKTY NA 0
      })),
      allQuestions: allQuestionsData,
      drawnIds:
        savedState.drawnIds instanceof Set
          ? savedState.drawnIds
          : new Set(savedState.drawnIds || []),
    };
    return mergedState;
  }
  return baseState;
};

// --- Reducer (dodajemy nowe akcje) ---
function gameReducer(state, action) {
  switch (action.type) {
    case "SET_SCREEN":
      return { ...state, screen: action.payload };
    case "SET_CONFIG_STEP":
      return { ...state, configStep: action.payload };
    case "ADD_PLAYER":
      const newPlayer = {
        id: crypto.randomUUID(),
        name: action.payload,
        skipsUsed: 0,
        tasksCompleted: 0,
        tasksSkipped: 0,
        points: 0,
      };
      return { ...state, players: [...state.players, newPlayer] };
    case "REMOVE_PLAYER":
      return {
        ...state,
        players: state.players.filter((p) => p.id !== action.payload),
      };
    case "UPDATE_SETTINGS":
      return { ...state, settings: { ...state.settings, ...action.payload } };
    case "START_GAME":
      return {
        ...state,
        gameStarted: true,
        screen: "game",
        currentPlayerIndex: 0,
        drawnIds: new Set(),
      };
    case "SET_CURRENT_PLAYER":
      return { ...state, currentPlayerIndex: action.payload };
    case "SET_SELECTED_PLAYER":
      return { ...state, selectedPlayerIndex: action.payload };
    case "SHOW_TASK_TYPE_SELECTION":
      return { ...state, showTaskTypeSelection: action.payload };
    case "SHOW_QUESTION_DIALOG":
      return { ...state, showQuestionDialog: action.payload };
    case "SET_CURRENT_QUESTION":
      return { ...state, currentQuestion: action.payload };
    case "NEXT_TURN": {
      // Używamy bloku, aby móc deklarować zmienne
      let newCurrentPlayerIndex;

      if (
        state.settings.gameMode === "classic" &&
        state.selectedPlayerIndex !== null
      ) {
        // Dla trybu 'classic':
        // Gracz, który został wybrany przez butelkę i wykonał zadanie (state.selectedPlayerIndex),
        // staje się teraz osobą kręcącą (nowym state.currentPlayerIndex).
        newCurrentPlayerIndex = state.selectedPlayerIndex;
        console.log(
          `🔄 NEXT_TURN (Classic): Wybrany gracz (${
            state.players[state.selectedPlayerIndex]?.name
          }) teraz kręci.`
        );
      } else {
        // Dla innych trybów gry LUB jeśli w trybie 'classic' nie było wybranego gracza przez butelkę
        // (np. na samym początku gry, zanim ktokolwiek został wybrany, lub w niestandardowym przepływie),
        // używamy standardowej, sekwencyjnej zmiany tury.
        if (state.players.length > 0) {
          newCurrentPlayerIndex =
            (state.currentPlayerIndex + 1) % state.players.length;
        } else {
          newCurrentPlayerIndex = 0; // Fallback, jeśli nie ma graczy
        }
        console.log(
          `🔄 NEXT_TURN (Sequential/Inny tryb): Następny gracz to ${state.players[newCurrentPlayerIndex]?.name}.`
        );
      }

      return {
        ...state,
        previousPlayerIndex: state.currentPlayerIndex, // Zapisujemy, kto kręcił poprzednio
        currentPlayerIndex: newCurrentPlayerIndex,
        selectedPlayerIndex: null, // Resetujemy gracza wybranego przez butelkę
        currentQuestion: null, // Czyścimy aktualne pytanie/zadanie
        showTaskTypeSelection: false, // Ukrywamy wybór P/W (GameScreen zdecyduje, czy pokazać)
        showQuestionDialog: false, // Ukrywamy ekran pytania/zadania
      };
    }

    case "COMPLETE_TASK": {
      const { completed } = action.payload; // completed to boolean
      const playerIndexToUpdate =
        state.selectedPlayerIndex !== null
          ? state.selectedPlayerIndex
          : state.currentPlayerIndex;

      // Pobierz typ bieżącego zadania (pytanie czy wyzwanie)
      const taskType = state.currentQuestion?.type;
      let pointsChange = 0;

      if (taskType === "pytanie") {
        pointsChange = completed ? 1 : -1;
      } else if (taskType === "wyzwanie") {
        pointsChange = completed ? 3 : -3;
      }
      // Jeśli typ zadania jest inny (np. dla innych trybów, choć ta akcja
      // jest głównie dla P/W), punkty mogą się nie zmieniać lub możesz dodać inną logikę.
      // W tym przypadku, jeśli taskType nie jest ani 'pytanie', ani 'wyzwanie',
      // pointsChange pozostanie 0 (lub możesz ustawić domyślną karę/nagrodę).

      const updatedPlayers = state.players.map((player, index) => {
        if (index === playerIndexToUpdate) {
          return {
            ...player,
            tasksCompleted: completed
              ? player.tasksCompleted + 1
              : player.tasksCompleted,
            tasksSkipped: !completed
              ? player.tasksSkipped + 1
              : player.tasksSkipped,
            points: player.points + pointsChange, // <<< ZASTOSOWANIE NOWEJ LOGIKI PUNKTACJI
          };
        }
        return player;
      });

      console.log(
        `📝 Aktualizacja statystyk (COMPLETE_TASK) dla gracza ${state.players[playerIndexToUpdate]?.name}: Zrobione: ${completed}, Nowe punkty: ${updatedPlayers[playerIndexToUpdate]?.points}`
      );
      return { ...state, players: updatedPlayers };
    }

    case "SKIP_TASK": {
      const playerIndexToUpdate =
        state.selectedPlayerIndex !== null
          ? state.selectedPlayerIndex
          : state.currentPlayerIndex;

      const playersWithSkip = state.players.map((player, index) => {
        if (index === playerIndexToUpdate) {
          // Logika 'canSkip' jest w QuestionScreen, więc tutaj zakładamy, że skip jest dozwolony
          return {
            ...player,
            skipsUsed: player.skipsUsed + 1,
            tasksSkipped: player.tasksSkipped + 1, // Użycie "Pomiń" to też forma pominięcia zadania
            // <<< BRAK ZMIANY PUNKTÓW DLA DARMOWYCH POMINIĘĆ >>>
            // Punkty pozostają bez zmian, zgodnie z regulaminem (2 pominięcia bez kary)
          };
        }
        return player;
      });
      console.log(
        `⏭️ Aktualizacja statystyk (SKIP_TASK) dla gracza ${state.players[playerIndexToUpdate]?.name}`
      );
      return { ...state, players: playersWithSkip };
    }

    case "RESET_GAME":
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(PLAYERS_KEY);
      return getInitialState();

    case "GO_BACK_TO_START":
      return { ...state, screen: "start" };

    case "ADD_DRAWN_ID":
      const newDrawnIds = new Set(state.drawnIds);
      newDrawnIds.add(action.payload);
      return { ...state, drawnIds: newDrawnIds };

    case "CLEAR_DRAWN_IDS":
      return { ...state, drawnIds: new Set() };
    case "SET_DRAWN_IDS":
      return { ...state, drawnIds: action.payload };
    default:
      return state;
  }
}

// --- Kontekst i Provider (z poprawionym useReducer) ---
const GameContext = createContext(null);

export function GameProvider({ children }) {
  // Inicjalizujemy reducer z funkcją getInitialState
  const [state, dispatch] = useReducer(gameReducer, undefined, getInitialState);

  // Zapisywanie stanu (debounce) - teraz zapisuje też graczy
  useEffect(() => {
    if (state.settings.autoSave) {
      console.log("💾 Zapisywanie stanu gry...");
      const timeoutId = setTimeout(() => {
        saveGameState(state);
      }, 500); // 500ms debounce
      return () => clearTimeout(timeoutId);
    }
  }, [state]); // Zapisuj przy każdej zmianie, jeśli autoSave jest włączone

  // Nie potrzebujemy już osobnego zapisu graczy

  return (
    <GameContext.Provider value={{ state, dispatch }}>
      {children}
    </GameContext.Provider>
  );
}

// --- Hook (bez zmian) ---
export function useGame() {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error("useGame must be used within GameProvider");
  }
  return context;
}
